import React from "react";
import {
  AlertCircle,
  Bot,
  FlaskConical,
  Plus,
  Search,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import MessageBubble from "../../../shared/ui/MessageBubble";
import ResultsDashboard from "../../dashboard/components/ResultsDashboard";

const MAX_QUERY_CLARIFICATION_COUNT = 10;
const PURPOSE_OPTION_NUMERALS = {
  A: ["1", "１", "一"],
  B: ["2", "２", "二", "两", "兩"],
  C: ["3", "３", "三"],
};

const shouldHideMessage = (message, hiddenMessageTypeSet) =>
  hiddenMessageTypeSet.has(message.type) ||
  (message.type === "criteria_confirmation" && Boolean(message.locked));

function sanitizePurposeSupplement(value) {
  return String(value || "")
    .trim()
    .replace(/^[,，。:：;；-]\s*/, "")
    .trim();
}

function resolvePurposeSelectionInput(input, options) {
  const trimmed = String(input || "").trim();
  if (!trimmed || !Array.isArray(options) || options.length === 0) {
    return trimmed;
  }

  const normalized = trimmed.replace(/\s+/g, " ");
  const exactCandidate = options.find(
    (option) => String(option?.clarified_query_candidate || "").trim() === normalized,
  );
  if (exactCandidate) {
    return normalized;
  }

  for (let index = 0; index < options.length; index += 1) {
    const option = options[index];
    const optionId = String(option?.option_id || "").toUpperCase();
    const candidate = String(option?.clarified_query_candidate || "").trim();
    if (!optionId || !candidate) {
      continue;
    }

    const numeralPattern = PURPOSE_OPTION_NUMERALS[optionId]?.join("|") || `${index + 1}`;
    const patterns = [
      new RegExp(
        `^(?:option\\s*)?${optionId}(?:\\s*(?:案|option))?(?:\\s*(?:[\\-:：,，.]\\s*|\\s+)(.+))?$`,
        "iu",
      ),
      new RegExp(
        `^(?:選|选|choose|pick|use|go\\s+with)?\\s*${optionId}(?:\\s*(?:案|option))?(?:\\s*(?:[\\-:：,，.]\\s*|\\s+)(.+))?$`,
        "iu",
      ),
      new RegExp(
        `^(?:選|选|choose|pick|use)?\\s*(?:第\\s*)?(?:${numeralPattern})(?:\\s*(?:个|個|つ|番|案|option))?(?:\\s*(?:[\\-:：,，.]\\s*|\\s+)(.+))?$`,
        "iu",
      ),
      new RegExp(
        `^(?:第\\s*)?(?:${numeralPattern})(?:\\s*(?:个|個|つ|番|案))?(?:\\s*(?:[\\-:：,，.]\\s*|\\s+)(.+))?$`,
        "iu",
      ),
    ];

    for (const pattern of patterns) {
      const match = normalized.match(pattern);
      if (!match) {
        continue;
      }
      const supplement = sanitizePurposeSupplement(match[1] || "");
      return supplement ? `${candidate} ${supplement}`.trim() : candidate;
    }
  }

  return normalized;
}

function WelcomeSystemMessage({ t }) {
  const translateOptional = (key) => {
    const value = t(key);
    return value && value !== key ? value : "";
  };
  const tipItems = [t("app.welcome.hint1"), t("app.welcome.hint2")].filter(Boolean);
  const exampleItems = [
    {
      label: translateOptional("app.welcome.example1Label"),
      text: t("app.welcome.example1"),
    },
    {
      label: translateOptional("app.welcome.example2Label"),
      text: t("app.welcome.example2"),
    },
    {
      label: translateOptional("app.welcome.example3Label"),
      text: translateOptional("app.welcome.example3"),
    },
    {
      label: translateOptional("app.welcome.example4Label"),
      text: translateOptional("app.welcome.example4"),
    },
  ].filter((item) => item.text);
  const updatesTitle = translateOptional("app.welcome.updatesTitle");
  const paperAnalysisTitle = translateOptional("app.welcome.paperAnalysisTitle");
  const paperAnalysisDescription = translateOptional("app.welcome.paperAnalysisDescription");
  const featureTags = [
    translateOptional("app.welcome.featureTag1"),
    translateOptional("app.welcome.featureTag2"),
    translateOptional("app.welcome.featureTag3"),
  ].filter(Boolean);
  const exampleIconComponents = [Search, TrendingUp, Users, FlaskConical];
  return (
    <div className="mx-auto w-full max-w-[920px] space-y-8">
      {(updatesTitle || paperAnalysisTitle || paperAnalysisDescription || featureTags.length > 0) && (
        <section className="flex justify-center">
          <div className="inline-flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full border border-[#d3e3fd] bg-white px-4 py-2 text-[13px] text-[#4a5568] shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all hover:border-[#c0d7fc] hover:shadow-[0_4px_12px_rgba(192,215,252,0.35)]">
            {updatesTitle && (
              <span className="rounded-full border border-[#d3e3fd] bg-[#e9eef6] px-2 py-0.5 text-[11px] font-bold uppercase leading-5 text-gray-800">
                {updatesTitle}
              </span>
            )}
            {paperAnalysisTitle && (
              <span className="font-medium">{paperAnalysisTitle}</span>
            )}
            {featureTags.length > 0 && (
              <>
                <span className="hidden text-[#cbd5e0] sm:inline">|</span>
                <span className="text-xs font-medium text-teal-700">
                  {featureTags.join(" / ")}
                </span>
              </>
            )}
          </div>
          {paperAnalysisDescription && (
            <span className="sr-only">{paperAnalysisDescription}</span>
          )}
        </section>
      )}

      {tipItems.length > 0 && (
        <div className="flex flex-wrap justify-center gap-2">
          {tipItems.map((item) => (
            <span
              key={item}
              className="rounded-full bg-white px-3 py-1 text-xs font-medium leading-5 text-[#718096] shadow-[0_2px_8px_rgba(0,0,0,0.025)]"
            >
              {item}
            </span>
          ))}
        </div>
      )}

      <section>
        <h3 className="mb-5 text-center text-sm font-semibold text-[#718096]">
          {t("app.welcome.examplesTitle")}
        </h3>
        <div className="grid gap-5 md:grid-cols-2">
          {exampleItems.map((item, index) => {
            const Icon = exampleIconComponents[index] || Search;
            return (
            <div
              key={item.text}
              className="group relative flex min-h-[150px] items-start gap-5 rounded-[14px] border border-[#e2e8f0] bg-white p-6 text-left shadow-[0_4px_20px_rgba(0,0,0,0.025)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#c0d7fc] hover:shadow-[0_10px_30px_rgba(192,215,252,0.30)]"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-[#e9eef6] text-[#4f46e5] shadow-[inset_0_0_0_1px_rgba(211,227,253,0.9)]">
                <Icon className="h-6 w-6" strokeWidth={2.3} />
              </div>
              <div className="min-w-0">
                {item.label && (
                  <h4 className="text-base font-bold leading-6 text-[#1a202c]">
                    {item.label}
                  </h4>
                )}
                <p className={`${item.label ? "mt-3" : "m-0"} text-sm leading-7 text-[#4a5568]`}>
                  {item.text}
                </p>
              </div>
            </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function CriteriaConfirmationCard({ message, onSubmit, t, language }) {
  const [localError, setLocalError] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isReviewing, setIsReviewing] = React.useState(false);
  const criteriaDraftDigest = React.useMemo(() => {
    try {
      return JSON.stringify(Array.isArray(message.criteriaDraft) ? message.criteriaDraft : []);
    } catch (_error) {
      return "[]";
    }
  }, [message.criteriaDraft]);
  const [criteria, setCriteria] = React.useState(() =>
    Array.isArray(message.criteriaDraft) ? message.criteriaDraft.map((item) => ({ ...item })) : [],
  );

  React.useEffect(() => {
    setLocalError("");
    setIsSubmitting(false);
    const parsed = JSON.parse(criteriaDraftDigest);
    const next = Array.isArray(parsed) ? parsed : [];
    setCriteria(next);
    setActiveIndex(0);
    setIsReviewing(false);
  }, [criteriaDraftDigest, message.runId]);

  const isLocked = Boolean(message.locked);
  const disabled = isSubmitting || message.pending || isLocked;

  const humanize = (value) => {
    const raw = String(value || "");
    const mapping =
      language === "ja"
        ? {
            rct: "RCT",
            systematic_review: "システマティックレビュー",
            meta_analysis: "メタ解析",
            observational: "観察研究",
            any: "指定なし",
            last_3_years: "直近3年",
            last_5_years: "直近5年",
            last_10_years: "直近10年",
            custom_range: "期間指定",
          }
        : {
            rct: "RCT",
            systematic_review: "Systematic review",
            meta_analysis: "Meta-analysis",
            observational: "Observational",
            any: "Any",
            last_3_years: "Last 3 years",
            last_5_years: "Last 5 years",
            last_10_years: "Last 10 years",
            custom_range: "Custom range",
          };
    return mapping[raw] || raw.replace(/_/g, " ");
  };

  const topicLabel = (criterion) => {
    const topicJa = String(criterion?.topic_ja || "").trim();
    if (language === "ja" && topicJa) {
      return topicJa;
    }

    const raw = String(criterion?.topic || "");
    const mapping = {
      study_design: language === "ja" ? "研究デザイン" : "Study design",
      population: language === "ja" ? "対象集団" : "Population",
      outcome_focus: language === "ja" ? "評価項目" : "Outcome focus",
      publication_year: language === "ja" ? "出版年" : "Publication year",
    };
    return mapping[raw] || humanize(raw);
  };

  const renderImportanceLabel = (importance) => {
    const normalized = String(importance || "Optional");
    if (normalized === "Critical") return t("app.criteriaConfirmation.importanceHigh");
    if (normalized === "Minor") return t("app.criteriaConfirmation.importanceLow");
    return t("app.criteriaConfirmation.importanceMedium");
  };

  const updateCriterion = (idx, patch) => {
    setCriteria((prev) => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || {}), ...(patch || {}) };
      return next;
    });
  };

  const resolveMultiSelected = (pref) => {
    if (Array.isArray(pref)) {
      return pref.map((v) => String(v)).filter(Boolean);
    }
    if (typeof pref === "string" && pref.trim()) {
      return [pref.trim()];
    }
    return [];
  };

  const isAnySemanticValue = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return false;
    return (
      normalized === "any" ||
      normalized === "unspecified" ||
      normalized === "no_preference" ||
      normalized === "指定なし" ||
      normalized === "不限" ||
      normalized === "不指定"
    );
  };

  const resolveAnyOption = (criterion) => {
    const options = Array.isArray(criterion?.value_set) ? criterion.value_set : [];
    for (const option of options) {
      if (isAnySemanticValue(option)) {
        return String(option);
      }
    }
    return null;
  };

  const toggleMultiValue = (idx, option) => {
    const opt = String(option || "");
    const criterion = criteria[idx];
    const anyOption = resolveAnyOption(criterion);
    const isAnyOption = (value) => {
      const raw = String(value || "");
      if (anyOption && raw === anyOption) return true;
      return isAnySemanticValue(raw);
    };

    const current = resolveMultiSelected(criterion?.preference);
    const hasAny = current.some((v) => isAnyOption(v));
    const isSelected = current.includes(opt);

    let nextSelected = current;
    if (isAnyOption(opt)) {
      nextSelected = isSelected ? [] : [opt];
    } else {
      nextSelected = current.filter((v) => !isAnyOption(v));
      if (isSelected) {
        nextSelected = nextSelected.filter((v) => v !== opt);
      } else {
        nextSelected = [...nextSelected, opt];
      }
      if (nextSelected.length === 0 && hasAny && anyOption) {
        nextSelected = [anyOption];
      }
      if (nextSelected.length === 0 && anyOption) {
        nextSelected = [anyOption];
      }
    }

    updateCriterion(idx, { preference: nextSelected });
  };

  const resolvePublicationYearMode = (pref) => {
    if (pref && typeof pref === "object") {
      if (typeof pref.window === "string" && pref.window) {
        return pref.window;
      }
      if (typeof pref.min === "number" || typeof pref.max === "number") {
        return "custom_range";
      }
    }
    if (typeof pref === "string" && pref) {
      return pref;
    }
    return "any";
  };

  const setPublicationYearMode = (idx, mode) => {
    const value = String(mode || "any");
    if (value === "custom_range") {
      const existing = criteria[idx]?.preference;
      const min =
        existing && typeof existing === "object" && typeof existing.min === "number"
          ? existing.min
          : new Date().getFullYear() - 5;
      const max =
        existing && typeof existing === "object" && typeof existing.max === "number"
          ? existing.max
          : new Date().getFullYear();
      updateCriterion(idx, { preference: { min, max } });
      return;
    }
    if (value === "any") {
      updateCriterion(idx, { preference: "any" });
      return;
    }
    updateCriterion(idx, { preference: { window: value } });
  };

  const setPublicationYearRange = (idx, patch) => {
    const existing = criteria[idx]?.preference;
    const current =
      existing && typeof existing === "object" ? { ...existing } : { min: null, max: null };
    const next = { ...current, ...(patch || {}) };
    updateCriterion(idx, { preference: next });
  };

  const submit = async (confirmed) => {
    if (!onSubmit || isSubmitting || message.pending) return;

    const criteriaSet = Array.isArray(criteria) ? criteria : [];
    if (confirmed && (!criteriaSet || criteriaSet.length === 0)) {
      setLocalError(t("app.criteriaConfirmation.invalidCriteria"));
      return;
    }

    setLocalError("");
    setIsSubmitting(true);
    try {
      await onSubmit({
        runId: message.runId,
        threadId: message.threadId,
        confirmed,
        criteriaSet,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const criteriaDraft = Array.isArray(criteria) ? criteria : [];
  const criteriaCount = criteriaDraft.length;
  const currentIndex =
    criteriaCount > 0 ? Math.min(Math.max(activeIndex, 0), criteriaCount - 1) : 0;
  const currentCriterion = criteriaCount > 0 ? criteriaDraft[currentIndex] : null;
  const valueSet = Array.isArray(currentCriterion?.value_set) ? currentCriterion.value_set : null;
  const isPublicationYear = String(currentCriterion?.topic || "") === "publication_year";
  const selected = resolveMultiSelected(currentCriterion?.preference);
  const yearMode = resolvePublicationYearMode(currentCriterion?.preference);
  const pref = currentCriterion?.preference;
  const yearMin =
    pref && typeof pref === "object" && typeof pref.min === "number" ? pref.min : "";
  const yearMax =
    pref && typeof pref === "object" && typeof pref.max === "number" ? pref.max : "";

  React.useEffect(() => {
    if (criteriaCount === 0 && activeIndex !== 0) {
      setActiveIndex(0);
      return;
    }
    if (criteriaCount > 0 && activeIndex > criteriaCount - 1) {
      setActiveIndex(criteriaCount - 1);
    }
  }, [activeIndex, criteriaCount]);

  React.useEffect(() => {
    if (isLocked && criteriaCount > 0) {
      setIsReviewing(true);
    }
  }, [isLocked, criteriaCount]);

  const hasPreferenceSelection = (preferenceValue) => {
    if (Array.isArray(preferenceValue)) {
      return preferenceValue.map((v) => String(v).trim()).filter(Boolean).length > 0;
    }
    if (typeof preferenceValue === "string") {
      return preferenceValue.trim().length > 0;
    }
    if (preferenceValue && typeof preferenceValue === "object") {
      if (typeof preferenceValue.window === "string" && preferenceValue.window.trim()) {
        return true;
      }
      const hasMin = Number.isFinite(Number(preferenceValue.min));
      const hasMax = Number.isFinite(Number(preferenceValue.max));
      return hasMin || hasMax;
    }
    return false;
  };

  const summarizePreference = (preferenceValue) => {
    if (Array.isArray(preferenceValue)) {
      return preferenceValue.map((v) => humanize(v)).join(", ");
    }
    if (typeof preferenceValue === "string") {
      return humanize(preferenceValue);
    }
    if (preferenceValue && typeof preferenceValue === "object") {
      if (typeof preferenceValue.window === "string" && preferenceValue.window.trim()) {
        return humanize(preferenceValue.window);
      }
      const minText = Number.isFinite(Number(preferenceValue.min)) ? String(preferenceValue.min) : "-";
      const maxText = Number.isFinite(Number(preferenceValue.max)) ? String(preferenceValue.max) : "-";
      return `${minText} - ${maxText}`;
    }
    return "-";
  };

  const confirmCurrentStep = () => {
    if (!currentCriterion || disabled) return;
    if (!hasPreferenceSelection(currentCriterion.preference)) {
      setLocalError(t("app.criteriaConfirmation.selectPreferenceFirst"));
      return;
    }
    setLocalError("");
    if (currentIndex >= criteriaCount - 1) {
      setIsReviewing(true);
      return;
    }
    setActiveIndex((prev) => Math.min(prev + 1, criteriaCount - 1));
  };

  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="w-full max-w-5xl">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-8 w-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Bot className="h-4 w-4 text-blue-600" />
          </div>
          <div className="ai-message-enter relative flex-1 min-w-0 rounded-2xl rounded-tl-md border border-blue-100 bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-sm">
            <span className="absolute -left-1.5 top-4 h-3 w-3 rotate-45 border-l border-b border-blue-100 bg-white" />
            <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-900">
          {message.promptMessage || t("app.criteriaConfirmation.message")}
        </p>
        {message.reasoning && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {message.reasoning}
          </p>
        )}

        <div className="space-y-3 rounded-[2rem] border border-gray-200 p-4 sm:p-5">
            {criteriaCount > 0 && !isReviewing && (
              <div className="flex justify-end">
                <p className="text-xs text-gray-500">
                  {t("app.criteriaConfirmation.stepLabel", {
                    current: currentIndex + 1,
                    total: criteriaCount,
                  })}
                </p>
              </div>
            )}
            {criteriaDraft.length === 0 ? (
              <p className="text-sm text-gray-700">
                {t("app.criteriaConfirmation.empty")}
              </p>
            ) : isReviewing ? (
              <div className="space-y-3">
                {!isLocked && (
                  <div className="rounded-[1.5rem] bg-white p-4 space-y-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {t("app.criteriaConfirmation.reviewTitle")}
                    </p>
                    <p className="text-xs text-gray-600">
                      {t("app.criteriaConfirmation.reviewMessage")}
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  {criteriaDraft.map((criterion, idx) => (
                    <div
                      key={criterion?.id || `review-${message.runId}-${idx}`}
                      className="rounded-[1.5rem] bg-white p-4"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-700">
                          <span className="font-medium text-gray-900">
                            {topicLabel(criterion)}
                          </span>
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          {renderImportanceLabel(criterion?.importance)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-700">
                        {t("app.criteriaConfirmation.preferenceLabel")}: {summarizePreference(criterion?.preference)}
                      </p>
                      {criterion?.question && (
                        <p className="mt-1 text-xs text-gray-600 whitespace-pre-wrap">
                          {criterion.question}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {isLocked ? (
                  <div className="flex justify-end">
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100">
                      {t("app.criteriaConfirmation.finalized")}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => setIsReviewing(false)}
                      disabled={disabled}
                      className="px-4 py-2 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("app.criteriaConfirmation.reviseAnswers")}
                    </button>
                    <button
                      type="button"
                      onClick={() => submit(true)}
                      disabled={disabled}
                      className="px-4 py-2 text-xs font-medium rounded-full gemini-cta text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("app.criteriaConfirmation.confirm")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full gemini-progress-fill rounded-full transition-all"
                    style={{
                      width: `${Math.round(((currentIndex + 1) / Math.max(criteriaCount, 1)) * 100)}%`,
                    }}
                  />
                </div>

                <div
                  key={currentCriterion?.id || `crit-${message.runId}-${currentIndex}`}
                  className="rounded-[1.5rem] bg-white p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-gray-700">
                          <span className="font-medium text-gray-900">
                            {topicLabel(currentCriterion)}
                          </span>
                        </span>
                      </div>
                      {currentCriterion?.question && (
                        <div className="mt-2">
                          <p className="text-[11px] text-gray-500">
                            {t("app.criteriaConfirmation.scoringQuestionHint")}
                          </p>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                            {currentCriterion.question}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0 sm:min-w-[190px] sm:justify-end">
                      <label className="text-[11px] text-gray-500 whitespace-nowrap">
                        {t("app.criteriaConfirmation.importanceLabel")}
                      </label>
                      <select
                        value={String(currentCriterion?.importance || "Optional")}
                        onChange={(event) => updateCriterion(currentIndex, { importance: event.target.value })}
                        disabled={disabled}
                        className="min-w-[150px] px-3 py-1.5 text-xs border border-gray-200 rounded-full bg-white disabled:bg-gray-100"
                      >
                        <option value="Critical">{t("app.criteriaConfirmation.importanceHigh")}</option>
                        <option value="Optional">{t("app.criteriaConfirmation.importanceMedium")}</option>
                        <option value="Minor">{t("app.criteriaConfirmation.importanceLow")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-3">
                    {valueSet && !isPublicationYear && (
                      <div className="flex flex-wrap gap-2">
                        {valueSet.map((opt) => {
                          const optValue = String(opt);
                          const checked = selected.includes(optValue);
                          return (
                            <label
                              key={`${currentCriterion?.id || currentIndex}-${optValue}`}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs cursor-pointer select-none ${
                                checked
                                  ? "gemini-chip-selected text-white"
                                  : "bg-white text-gray-800 border-gray-200 hover:border-gray-300"
                              } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleMultiValue(currentIndex, optValue)}
                                disabled={disabled}
                                className="hidden"
                              />
                              {humanize(optValue)}
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {valueSet && isPublicationYear && (
                      <div className="space-y-2">
                        <select
                          value={yearMode}
                          onChange={(event) => setPublicationYearMode(currentIndex, event.target.value)}
                          disabled={disabled}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-2xl bg-white disabled:bg-gray-100"
                        >
                          {valueSet.map((opt) => (
                            <option key={`${currentCriterion?.id || currentIndex}-${opt}`} value={String(opt)}>
                              {humanize(opt)}
                            </option>
                          ))}
                        </select>

                        {yearMode === "custom_range" && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">
                                {t("app.criteriaConfirmation.yearMin")}
                              </label>
                              <input
                                type="number"
                                value={yearMin}
                                onChange={(event) =>
                                  setPublicationYearRange(currentIndex, { min: Number(event.target.value) })
                                }
                                disabled={disabled}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-2xl disabled:bg-gray-100"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] text-gray-500 mb-1">
                                {t("app.criteriaConfirmation.yearMax")}
                              </label>
                              <input
                                type="number"
                                value={yearMax}
                                onChange={(event) =>
                                  setPublicationYearRange(currentIndex, { max: Number(event.target.value) })
                                }
                                disabled={disabled}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-2xl disabled:bg-gray-100"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!valueSet && (
                      <div className="space-y-1">
                        <label className="block text-[11px] text-gray-500">
                          {t("app.criteriaConfirmation.preferenceLabel")}
                        </label>
                        <input
                          type="text"
                          value={
                            typeof currentCriterion?.preference === "string"
                              ? currentCriterion.preference
                              : JSON.stringify(currentCriterion?.preference ?? "")
                          }
                          onChange={(event) => updateCriterion(currentIndex, { preference: event.target.value })}
                          disabled={disabled}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-2xl disabled:bg-gray-100"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveIndex((prev) => Math.max(prev - 1, 0))}
                    disabled={disabled || currentIndex <= 0}
                    className="px-4 py-2 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("app.criteriaConfirmation.previous")}
                  </button>
                  <button
                    type="button"
                    onClick={confirmCurrentStep}
                    disabled={disabled}
                    className="px-4 py-2 text-xs font-medium rounded-full gemini-cta text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("app.criteriaConfirmation.stepConfirm")}
                  </button>
                </div>
              </div>
            )}
          </div>

        {localError && <p className="text-sm text-red-600">{localError}</p>}
        {message.error && (
          <p className="text-sm text-red-600">{message.error}</p>
        )}
        {(isSubmitting || message.pending) && (
          <span className="text-xs text-gray-500">
            {t("app.searching")}
          </span>
        )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PurposePromptCard({ message, onSubmit, t }) {
  const [text, setText] = React.useState("");
  const [localError, setLocalError] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    setLocalError("");
    setIsSubmitting(false);
    if (typeof message?.submittedPurpose === "string") {
      setText(message.submittedPurpose);
    } else if (typeof message?.draftPurpose === "string") {
      setText(message.draftPurpose);
    } else {
      setText("");
    }
  }, [message?.draftPurpose, message?.id, message?.submittedPurpose]);

  const isLocked = Boolean(message?.locked);
  const disabled = Boolean(message?.pending) || isSubmitting || isLocked;
  const isCollectionStep = message?.type === "purpose_collection";
  const purposeOptions = Array.isArray(message?.purposeOptions) ? message.purposeOptions : [];
  const purposePromptText = isCollectionStep
    ? message?.promptMessage || t("app.purposePrompt.message")
    : message?.promptMessage || t("app.purposePrompt.message");
  const selectedOption = purposeOptions.find(
    (option) => String(option?.clarified_query_candidate || "").trim() === String(text || "").trim(),
  );
  const selectedOptionId = purposeOptions.find(
    (option) => String(option?.clarified_query_candidate || "").trim() === String(text || "").trim(),
  )?.option_id;

  const submit = async (skip = false) => {
    if (!onSubmit || disabled) return;
    setLocalError("");
    setIsSubmitting(true);
    try {
      const resolvedPurpose = skip
        ? ""
        : isCollectionStep
          ? resolvePurposeSelectionInput(text, purposeOptions)
          : text;
      await onSubmit({
        runId: message.runId,
        threadId: message.threadId,
        messageType: message.type,
        userPurpose: resolvedPurpose,
      });
    } catch (_error) {
      setLocalError(t("app.purposePrompt.submitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCollectionKeyDown = (event) => {
    if (!isCollectionStep || disabled) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit(false);
    }
  };

  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="w-full max-w-5xl">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-8 w-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Bot className="h-4 w-4 text-blue-600" />
          </div>
          <div className="ai-message-enter relative flex-1 min-w-0 rounded-2xl rounded-tl-md border border-blue-100 bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-sm">
            <span className="absolute -left-1.5 top-4 h-3 w-3 rotate-45 border-l border-b border-blue-100 bg-white" />
            <div className="space-y-3">
              <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
                {purposePromptText}
              </p>
              <p className="text-xs text-gray-600">
                {message.helperText || t("app.purposePrompt.helper")}
              </p>

              {isCollectionStep ? (
                <>
                  {purposeOptions.length > 0 && (
                    <div className="space-y-2">
                      {purposeOptions.map((option) => {
                        const isActive = selectedOptionId === option.option_id;
                        return (
                          <button
                            key={`${message.id}-${option.option_id}`}
                            type="button"
                            onClick={() => {
                              setText(option.clarified_query_candidate || "");
                              setLocalError("");
                            }}
                            disabled={disabled}
                            className={`w-full rounded-xl border px-3 py-3 text-left transition-colors ${
                              isActive
                                ? "border-blue-300 bg-blue-50 shadow-[0_0_0_1px_rgba(59,130,246,0.12)]"
                                : "border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/40"
                            } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
                          >
                            <div className="flex items-start gap-3">
                              <span className={`mt-0.5 inline-flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-semibold ${
                                isActive ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-700"
                              }`}>
                                {option.option_id}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium uppercase tracking-[0.03em] text-slate-500">
                                      {t("app.purposePrompt.directionLabel")}
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-slate-900">
                                      {option.label}
                                    </p>
                                  </div>
                                  {isActive && (
                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-[11px] font-medium text-blue-700">
                                      {t("app.purposePrompt.selectedBadge")}
                                    </span>
                                  )}
                                </div>
                                <div className="mt-3">
                                  <p className="text-xs font-medium uppercase tracking-[0.03em] text-slate-500">
                                    {t("app.purposePrompt.descriptionLabel")}
                                  </p>
                                  <p className="mt-1 text-sm leading-6 text-slate-600">
                                    {option.description}
                                  </p>
                                </div>
                                <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                                  <p className="text-xs font-medium uppercase tracking-[0.03em] text-slate-500">
                                    {t("app.purposePrompt.candidateLabel")}
                                  </p>
                                  <p className="mt-1 text-sm text-slate-800">
                                    {option.clarified_query_candidate}
                                  </p>
                                </div>
                                <p className="mt-3 text-xs text-slate-500">
                                  {t("app.purposePrompt.optionActionHint")}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {!isLocked && (
                    <div className="space-y-2">
                      {selectedOption && (
                        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                          {t("app.purposePrompt.selectionSummary", {
                            optionId: selectedOption.option_id,
                          })}
                        </div>
                      )}
                      <div className="flex items-end gap-2 rounded-[1.5rem] border border-gray-200 bg-white px-3 py-2 focus-within:border-blue-300 focus-within:ring-2 focus-within:ring-blue-100">
                        <textarea
                          rows={2}
                          value={text}
                          onChange={(event) => setText(event.target.value)}
                          onKeyDown={handleCollectionKeyDown}
                          disabled={disabled}
                          placeholder={message.placeholder || t("app.purposePrompt.placeholder")}
                          className="w-full resize-none border-none bg-transparent px-1 py-1 text-sm text-gray-800 outline-none placeholder:text-gray-400"
                        />
                        <button
                          type="button"
                          onClick={() => submit(false)}
                          disabled={disabled}
                          className="h-9 w-9 rounded-full gemini-cta text-white disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={t("app.purposePrompt.send")}
                        >
                          <svg className="mx-auto h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2.5}
                              d="M5 12h14M12 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => submit(true)}
                          disabled={disabled}
                          className="px-3 py-1 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {t("app.purposePrompt.skip")}
                        </button>
                      </div>
                    </div>
                  )}
                  {isLocked && (
                    <div className="flex justify-end">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100">
                        {t("app.criteriaConfirmation.finalized")}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <textarea
                    rows={3}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    disabled={disabled}
                    placeholder={message.placeholder || t("app.purposePrompt.placeholder")}
                    className="w-full resize-y min-h-[88px] px-3 py-2 text-sm border border-gray-200 rounded-2xl bg-white disabled:bg-gray-100"
                  />
                  {isLocked ? (
                    <div className="flex justify-end">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100">
                        {t("app.criteriaConfirmation.finalized")}
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => submit(false)}
                      disabled={disabled}
                      className="px-4 py-2 text-xs font-medium rounded-full gemini-cta text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("app.purposePrompt.confirm")}
                    </button>
                  )}
                </>
              )}
              {localError && <p className="text-sm text-red-600">{localError}</p>}
              {message.error && <p className="text-sm text-red-600">{message.error}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QueryClarificationCard({ message, onSubmit, t }) {
  const [queries, setQueries] = React.useState([]);
  const [localError, setLocalError] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    setLocalError("");
    setIsSubmitting(false);
    if (Array.isArray(message?.submittedQueries)) {
      setQueries(message.submittedQueries);
      return;
    }
    setQueries(Array.isArray(message?.queryDraft) ? message.queryDraft : []);
  }, [message?.id, message?.queryDraft, message?.submittedQueries]);

  const isLocked = Boolean(message?.locked);
  const disabled = Boolean(message?.pending) || isSubmitting || isLocked;
  const canAddQuery = !disabled && queries.length < MAX_QUERY_CLARIFICATION_COUNT;

  const updateQuery = (index, nextValue) => {
    setQueries((prev) =>
      prev.map((query, currentIndex) => (currentIndex === index ? nextValue : query)),
    );
  };

  const addQuery = () => {
    if (!canAddQuery) {
      setLocalError(t("app.queryPrompt.maxCount", { count: MAX_QUERY_CLARIFICATION_COUNT }));
      return;
    }
    setLocalError("");
    setQueries((prev) => [...prev, ""]);
  };

  const removeQuery = (index) => {
    setQueries((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const submit = async () => {
    if (!onSubmit || disabled) return;
    const normalizedQueries = queries
      .map((query) => String(query || "").trim())
      .filter(Boolean);

    if (normalizedQueries.length === 0) {
      setLocalError(t("app.queryPrompt.validation"));
      return;
    }
    if (normalizedQueries.length > MAX_QUERY_CLARIFICATION_COUNT) {
      setLocalError(t("app.queryPrompt.maxCount", { count: MAX_QUERY_CLARIFICATION_COUNT }));
      return;
    }

    setLocalError("");
    setIsSubmitting(true);
    try {
      await onSubmit({
        runId: message.runId,
        threadId: message.threadId,
        queries: normalizedQueries,
      });
    } catch (_error) {
      setLocalError(t("app.queryPrompt.submitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-start animate-fadeIn">
      <div className="w-full max-w-5xl">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-8 w-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center flex-shrink-0">
            <Bot className="h-4 w-4 text-blue-600" />
          </div>
          <div className="ai-message-enter relative flex-1 min-w-0 rounded-2xl rounded-tl-md border border-blue-100 bg-white px-4 py-3 shadow-sm">
            <span className="absolute -left-1.5 top-4 h-3 w-3 rotate-45 border-l border-b border-blue-100 bg-white" />
            <div className="space-y-2.5">
              <p className="text-sm text-gray-900 whitespace-pre-wrap leading-snug">
                {message.promptMessage || t("app.queryPrompt.message")}
              </p>
              <p className="text-xs text-gray-600">
                {message.helperText || t("app.queryPrompt.helper")}
              </p>

              <div className="space-y-1.5">
                {queries.map((query, index) => (
                  <div
                    key={`${message.runId || "query"}-${index}`}
                    className="flex items-start gap-2 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5"
                  >
                    <span className="pt-2 text-[11px] font-medium text-gray-400">
                      {index + 1}
                    </span>
                    <textarea
                      rows={1}
                      value={query}
                      onChange={(event) => updateQuery(index, event.target.value)}
                      disabled={disabled}
                      placeholder={t("app.queryPrompt.placeholder")}
                      className="w-full resize-y min-h-[42px] max-h-28 border-none bg-transparent px-1 py-1.5 text-sm leading-snug text-gray-800 outline-none placeholder:text-gray-400"
                    />
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() => removeQuery(index)}
                        disabled={disabled}
                        className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={t("app.queryPrompt.remove")}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {!isLocked && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={addQuery}
                    disabled={!canAddQuery}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {t("app.queryPrompt.add")}
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={disabled}
                    className="px-4 py-1.5 text-xs font-medium rounded-full gemini-cta text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("app.queryPrompt.confirm")}
                  </button>
                </div>
              )}

              {isLocked && (
                <div className="flex justify-end">
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 border border-blue-100">
                    {t("app.criteriaConfirmation.finalized")}
                  </span>
                </div>
              )}

              {localError && <p className="text-sm text-red-600">{localError}</p>}
              {message.error && <p className="text-sm text-red-600">{message.error}</p>}
              {(isSubmitting || message.pending) && (
                <span className="text-xs text-gray-500">{t("app.searching")}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MessageList({
  messages,
  onRerunInPro,
  onPurposePromptSubmit,
  onCriteriaConfirmationSubmit,
  onQueryClarificationSubmit,
  onReferenceClick,
  onSelectImage,
  isHistoryView,
  currentSearchId,
  onOpenPaperAnalysis,
  hiddenMessageTypes = [],
  searchMode = "basic",
  onSearchModeChange,
  sourceSelectionNudgeKey = 0,
  sourceSelectionNudgeTargetResultId = null,
}) {
  const { t, i18n } = useTranslation();
  const hiddenMessageTypeSet = React.useMemo(
    () => new Set(Array.isArray(hiddenMessageTypes) ? hiddenMessageTypes : []),
    [hiddenMessageTypes],
  );
  const visibleMessages = React.useMemo(
    () => messages.filter((message) => !shouldHideMessage(message, hiddenMessageTypeSet)),
    [hiddenMessageTypeSet, messages],
  );

  return (
    <>
      {visibleMessages.map((message, index) => {
        return (
          <React.Fragment key={message.id || `msg-${index}`}>
            <div className="flex flex-col">
        {message.type === "system" && (
          <MessageBubble
            type="system"
            wrapperClassName={message.variant === "welcome" ? "max-w-[920px]" : "max-w-3xl"}
            className={
              message.variant === "welcome"
                ? "w-full border-transparent bg-transparent p-0 shadow-none"
                : ""
            }
          >
            {message.variant === "welcome" ? (
              <WelcomeSystemMessage
                t={t}
                searchMode={searchMode}
                onSearchModeChange={onSearchModeChange}
              />
            ) : (
              <div className="flex items-start space-x-3 sm:space-x-4">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                </div>
                <p className="text-sm sm:text-base text-blue-900 whitespace-pre-line leading-relaxed">
                  {message.content}
                </p>
              </div>
            )}
          </MessageBubble>
        )}

        {(message.type === "user" || message.type === "purpose_user_reply") && (
          <MessageBubble type="user">
            <p className="text-sm sm:text-base leading-relaxed">{message.content}</p>
          </MessageBubble>
        )}

        {message.type === "error" && (
          <MessageBubble type="error">
            <div className="flex items-start">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 mt-0.5 mr-2 sm:mr-3" />
              <div className="flex-1">
                <p className="text-sm sm:text-base text-red-800 font-medium mb-2">
                  {t("app.searchFailedTitle")}
                </p>
                <p className="text-sm sm:text-base text-red-700">{message.content}</p>
              </div>
            </div>
          </MessageBubble>
        )}

        {message.type === "criteria_confirmation" && (
          <CriteriaConfirmationCard
            message={message}
            onSubmit={onCriteriaConfirmationSubmit}
            t={t}
            language={i18n.language}
          />
        )}

        {(message.type === "purpose_collection" || message.type === "purpose_clarification") && (
          <PurposePromptCard
            message={message}
            onSubmit={onPurposePromptSubmit}
            t={t}
          />
        )}

        {message.type === "query_clarification" && (
          <QueryClarificationCard
            message={message}
            onSubmit={onQueryClarificationSubmit}
            t={t}
          />
        )}

        {message.type === "result" && (
          <div className="space-y-6">
            {message.papers && message.papers.length > 0 && (
              <ResultsDashboard
                message={message}
                messages={messages}
                onRerunInPro={onRerunInPro}
                handleReferenceClick={onReferenceClick}
                setSelectedImage={onSelectImage}
                isHistoryView={isHistoryView}
                currentSearchId={currentSearchId}
                onOpenPaperAnalysis={onOpenPaperAnalysis}
                sourceSelectionNudgeKey={
                  sourceSelectionNudgeTargetResultId === message.id
                    ? sourceSelectionNudgeKey
                    : 0
                }
              />
            )}
          </div>
        )}

            </div>
          </React.Fragment>
        );
      })}
    </>
  );
}
